/**
 * UnderwritingBot.jsx — Phase 2B
 * Four-tab page:
 *   1. Recommendations  (bucketed 3-rec engine with instant-issue priority)
 *   2. Carrier Knowledge Bot  (AI carrier research chat)
 *   3. Carrier Roster Manager (active / pending / blacklisted)
 *   4. Underwriting Doc Library (upload + extracted rules)
 */
import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import TopBar from '../components/TopBar.jsx';
import { apiGet, apiPost, apiPut, apiDel, api } from '../lib/api.js';
import toast from 'react-hot-toast';
import {
  Brain, BookOpen, Users, Send, Loader2, ExternalLink, AlertTriangle,
  CheckCircle2, Clock, Upload, Trash2, Plus, RefreshCw, XCircle,
  ChevronDown, FileBadge, Info, Shield, Sparkles, Phone, ChevronRight,
  Star, Zap, FileText
} from 'lucide-react';

const TABS = [
  { id: 'recommend', label: 'Get Recommendations', icon: Sparkles },
  { id: 'bot', label: 'Carrier Knowledge Bot', icon: Brain },
  { id: 'roster', label: 'Carrier Roster', icon: Users },
  { id: 'library', label: 'Doc Library', icon: BookOpen },
];

export default function UnderwritingBot() {
  const [tab, setTab] = useState('recommend');

  // Roster status summary for header
  const { data: rosterData } = useQuery({
    queryKey: ['carrier-roster'],
    queryFn: () => apiGet('/carrier-roster'),
  });
  const roster = rosterData?.data?.roster || [];
  const activeCount = roster.filter((r) => r.status === 'active').length;
  const inactiveCount = roster.filter((r) => r.status !== 'active').length;

  return (
    <div className="space-y-4">
      <TopBar
        title="Underwriting Bot"
        subtitle={activeCount > 0
          ? `Active carriers: ${activeCount}. Inactive: ${inactiveCount}.`
          : 'No active carriers yet — upload underwriting grids to get started.'}
      />

      {/* Roster status pill */}
      {activeCount > 0 && (
        <div className="flex items-center gap-2 text-xs text-white/50">
          <div className="w-2 h-2 rounded-full bg-emerald-400" />
          {activeCount} active carrier{activeCount !== 1 ? 's' : ''} ·{' '}
          <button onClick={() => setTab('roster')} className="text-civix-cyan hover:underline">
            Manage Roster
          </button>
        </div>
      )}

      {/* Tab bar */}
      <div className="flex gap-1 panel p-1 w-fit flex-wrap">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm transition ${tab === id ? 'bg-civix-purple/25 text-white border border-civix-purple/50' : 'text-white/60 hover:text-white'}`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      {tab === 'recommend' && <RecommendationEngine onGoToRoster={() => setTab('roster')} onGoToLibrary={() => setTab('library')} />}
      {tab === 'bot' && <CarrierKnowledgeBot />}
      {tab === 'roster' && <CarrierRosterManager onGoToLibrary={() => setTab('library')} />}
      {tab === 'library' && <UnderwritingDocLibrary />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab 0 — Recommendation Engine
// ─────────────────────────────────────────────────────────────────────────────
const BUCKET_COLORS = {
  'WL/FE': 'text-emerald-400 bg-emerald-500/15 border-emerald-500/30',
  'Term':  'text-blue-400 bg-blue-500/15 border-blue-500/30',
  'IUL':   'text-purple-400 bg-purple-500/15 border-purple-500/30',
};
const LIKELIHOOD_COLORS = {
  high:   'text-emerald-400 bg-emerald-500/15',
  medium: 'text-amber-400 bg-amber-500/15',
  low:    'text-red-400 bg-red-500/15',
};
const ISSUE_COLORS = {
  instant:     'text-cyan-300 bg-cyan-500/15',
  simplified:  'text-sky-300 bg-sky-500/15',
  'fully underwritten': 'text-white/50 bg-white/5',
};

function RecommendationEngine({ onGoToRoster, onGoToLibrary }) {
  const [mode, setMode] = useState('form'); // 'form' | 'call'
  const [form, setForm] = useState({
    age: '', gender: '', height: '', weight: '',
    healthConditions: '', medications: '', smokingStatus: 'non-smoker',
    desiredCoverage: '', desiredProductType: '',
  });
  const [transcript, setTranscript] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  function setField(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function runForm() {
    setBusy(true); setError(null); setResult(null);
    try {
      const r = await apiPost('/underwriting/recommend-client', { clientInfo: form });
      setResult(r);
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  async function runCall() {
    if (!transcript.trim()) { setError('Paste a call transcript first.'); return; }
    setBusy(true); setError(null); setResult(null);
    try {
      const r = await apiPost('/underwriting/recommend-from-call', { callTranscript: transcript });
      setResult(r);
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  return (
    <div className="space-y-5">
      {/* Mode switcher */}
      <div className="flex gap-2">
        <button onClick={() => setMode('form')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm border transition ${mode === 'form' ? 'bg-civix-purple/20 border-civix-purple/50 text-white' : 'border-white/10 text-white/50 hover:text-white'}`}>
          <FileText className="w-4 h-4" /> Manual Entry
        </button>
        <button onClick={() => setMode('call')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm border transition ${mode === 'call' ? 'bg-civix-purple/20 border-civix-purple/50 text-white' : 'border-white/10 text-white/50 hover:text-white'}`}>
          <Phone className="w-4 h-4" /> From Call Transcript
        </button>
      </div>

      <div className="grid lg:grid-cols-[1fr_1.4fr] gap-5">
        {/* Left: inputs */}
        <div className="space-y-4">
          {mode === 'form' ? (
            <div className="panel p-5 space-y-4">
              <div className="text-sm font-semibold text-white/80">Client Information</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label mb-1">Age</label>
                  <input type="number" value={form.age} onChange={(e) => setField('age', e.target.value)} placeholder="58" className="input w-full" />
                </div>
                <div>
                  <label className="label mb-1">Gender</label>
                  <select value={form.gender} onChange={(e) => setField('gender', e.target.value)} className="input w-full">
                    <option value="">Not specified</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="label mb-1">Height</label>
                  <input value={form.height} onChange={(e) => setField('height', e.target.value)} placeholder="5ft 8in" className="input w-full" />
                </div>
                <div>
                  <label className="label mb-1">Weight</label>
                  <input value={form.weight} onChange={(e) => setField('weight', e.target.value)} placeholder="195 lbs" className="input w-full" />
                </div>
              </div>
              <div>
                <label className="label mb-1">Smoking / Tobacco Status</label>
                <select value={form.smokingStatus} onChange={(e) => setField('smokingStatus', e.target.value)} className="input w-full">
                  <option value="non-smoker">Non-smoker</option>
                  <option value="smoker">Smoker</option>
                  <option value="occasional cigar">Occasional cigar</option>
                  <option value="chewing tobacco">Chewing tobacco</option>
                  <option value="vaper">Vaper (e-cigarette)</option>
                </select>
              </div>
              <div>
                <label className="label mb-1">Health Conditions</label>
                <textarea value={form.healthConditions} onChange={(e) => setField('healthConditions', e.target.value)}
                  placeholder="e.g. Type 2 diabetes (A1C 7.2), sleep apnea on CPAP, hypertension"
                  className="input w-full h-20 resize-none" />
              </div>
              <div>
                <label className="label mb-1">Current Medications</label>
                <textarea value={form.medications} onChange={(e) => setField('medications', e.target.value)}
                  placeholder="e.g. Metformin 500mg, Lisinopril 10mg"
                  className="input w-full h-16 resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label mb-1">Coverage Amount ($)</label>
                  <input type="number" value={form.desiredCoverage} onChange={(e) => setField('desiredCoverage', e.target.value)} placeholder="25000" className="input w-full" />
                </div>
                <div>
                  <label className="label mb-1">Product Type</label>
                  <select value={form.desiredProductType} onChange={(e) => setField('desiredProductType', e.target.value)} className="input w-full">
                    <option value="">Any</option>
                    <option value="final expense">Final Expense / Whole Life</option>
                    <option value="term">Term Life</option>
                    <option value="IUL">IUL</option>
                  </select>
                </div>
              </div>
              <button onClick={runForm} disabled={busy}
                className="btn-primary w-full flex items-center justify-center gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {busy ? 'Analyzing…' : 'Get 3 Recommendations'}
              </button>
            </div>
          ) : (
            <div className="panel p-5 space-y-4">
              <div className="text-sm font-semibold text-white/80">Call Transcript</div>
              <p className="text-xs text-white/50">Paste a call transcript. CIVIX will extract the client’s health profile and run the recommendation engine.</p>
              <textarea value={transcript} onChange={(e) => setTranscript(e.target.value)}
                placeholder="Agent: Hi, what’s your date of birth?\nClient: February 14, 1966…"
                className="input w-full h-56 resize-none font-mono text-xs" />
              <button onClick={runCall} disabled={busy || !transcript.trim()}
                className="btn-primary w-full flex items-center justify-center gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
                {busy ? 'Extracting & Analyzing…' : 'Analyze Call & Recommend'}
              </button>
              {result?.clientInfo && (
                <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-xs text-white/70 space-y-0.5">
                  <div className="text-white/40 uppercase tracking-wider text-[10px] mb-1">Extracted client info</div>
                  {Object.entries(result.clientInfo).filter(([, v]) => v).map(([k, v]) => (
                    <div key={k}><span className="text-white/50 capitalize">{k.replace(/([A-Z])/g, ' $1').trim()}: </span>{String(v)}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Guardrail notice */}
          <div className="panel p-3 text-xs text-white/40 flex items-start gap-2">
            <Shield className="w-3.5 h-3.5 shrink-0 mt-0.5 text-civix-cyan" />
            Grounded only in your uploaded carrier grids and active roster. No public web data used. Always verify with the carrier’s underwriting desk before issuing.
          </div>
        </div>

        {/* Right: results */}
        <div className="space-y-3">
          {error && (
            <div className="panel p-4 border border-red-500/30 bg-red-500/10 text-red-300 text-sm flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {error}
            </div>
          )}

          {!result && !busy && !error && (
            <div className="panel p-8 text-center text-white/30">
              <Sparkles className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <div className="text-sm">Fill in the client profile and click <strong className="text-white/50">Get 3 Recommendations</strong>.</div>
              <div className="text-xs mt-1">You’ll get one recommendation per product bucket (WL/FE, Term, IUL) ranked by approval likelihood, with instant-issue carriers prioritized.</div>
            </div>
          )}

          {busy && (
            <div className="panel p-8 text-center text-white/40">
              <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-civix-purple" />
              <div className="text-sm">Analyzing carrier grids…</div>
            </div>
          )}

          {result && !busy && (
            <>
              {/* Notes for agent */}
              {result.notesForAgent && (
                <div className="panel p-3 text-sm text-white/70 border border-civix-cyan/20 bg-civix-cyan/5">
                  <span className="text-civix-cyan font-semibold text-xs uppercase tracking-wider">Agent notes: </span>
                  {result.notesForAgent}
                </div>
              )}

              {result.insufficientData && (
                <div className="panel p-3 text-xs text-amber-300 border border-amber-500/30 bg-amber-500/10 flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  Insufficient data from uploaded grids. Recommendations may be incomplete. Upload more carrier documents for better results.
                </div>
              )}

              {result.noCarriersConfigured && (
                <div className="panel p-5 text-center border border-amber-500/30 bg-amber-500/10">
                  <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-400" />
                  <div className="text-sm font-semibold text-amber-300">No carriers configured</div>
                  <div className="text-xs text-white/50 mt-1 mb-3">Upload underwriting grids to activate carriers on your roster.</div>
                  <div className="flex justify-center gap-2">
                    <button onClick={onGoToRoster} className="btn-ghost text-xs">Manage Roster</button>
                    <button onClick={onGoToLibrary} className="btn-primary text-xs">Upload Grids</button>
                  </div>
                </div>
              )}

              {/* Recommendation cards */}
              {(result.recommendations || []).map((rec, i) => (
                <div key={i} className="panel p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-civix-purple/20 border border-civix-purple/30 flex items-center justify-center text-sm font-bold text-civix-purple">#{rec.rank}</div>
                      <div>
                        <div className="font-semibold text-sm">{rec.carrierName}</div>
                        {rec.coverageRange && <div className="text-xs text-white/50">{rec.coverageRange}</div>}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 justify-end">
                      {rec.bucket && (
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${BUCKET_COLORS[rec.bucket] || 'text-white/50 bg-white/5 border-white/10'}`}>
                          {rec.bucket}
                        </span>
                      )}
                      {rec.issueType && (
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${ISSUE_COLORS[rec.issueType] || 'text-white/50 bg-white/5'}`}>
                          {rec.issueType === 'instant' && <Zap className="w-2.5 h-2.5 inline mr-0.5" />}
                          {rec.issueType}
                        </span>
                      )}
                      {rec.approvalLikelihood && (
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${LIKELIHOOD_COLORS[rec.approvalLikelihood] || 'text-white/50 bg-white/5'}`}>
                          {rec.approvalLikelihood} likelihood
                        </span>
                      )}
                    </div>
                  </div>

                  {rec.reasoning && (
                    <p className="text-xs text-white/70 leading-relaxed">{rec.reasoning}</p>
                  )}

                  {rec.keyDisqualifiers?.length > 0 && (
                    <div className="space-y-1">
                      <div className="text-[10px] uppercase tracking-wider text-red-400">Disqualifiers</div>
                      {rec.keyDisqualifiers.map((d, j) => (
                        <div key={j} className="text-xs text-white/50 flex items-start gap-1.5">
                          <XCircle className="w-3 h-3 text-red-400 shrink-0 mt-0.5" /> {d}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between text-[10px] text-white/30 pt-1 border-t border-white/5">
                    <span>{rec.estimatedMonthlyPremium ? `Est. ${rec.estimatedMonthlyPremium}/mo` : ''}</span>
                    <span>Source: {rec.sourceFile || 'n/a'}</span>
                  </div>
                </div>
              ))}

              <div className="text-[10px] text-white/30 text-center">
                {result.docsUsed} doc{result.docsUsed !== 1 ? 's' : ''} consulted · {result.carriersActive} carrier{result.carriersActive !== 1 ? 's' : ''} active
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab 1 — Carrier Knowledge Bot
// ─────────────────────────────────────────────────────────────────────────────
function CarrierKnowledgeBot() {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi! I'm your Underwriting Research Assistant. Ask me about carrier guidelines, rate classes, health conditions — anything you'd want to know before quoting a client." }
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const QUICK = [
    'Best carrier for type 2 diabetes, A1C 7.2, age 58?',
    'Who takes COPD on oxygen for final expense?',
    'Cigar smoker — can he get non-tobacco rates?',
    'Sleep apnea, CPAP-compliant 2 yrs — rate class?',
  ];

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  async function send(text) {
    const q = (text || input).trim();
    if (!q || busy) return;
    setMessages((m) => [...m, { role: 'user', content: q }]);
    setInput('');
    setBusy(true);
    try {
      const r = await apiPost('/ai/carrier-research', { question: q });
      const data = r.data || r;
      setMessages((m) => [...m, {
        role: 'assistant',
        content: data.answer || '(no answer)',
        details: data.details,
        carrierBreakdown: data.carrierBreakdown,
        recommendedAction: data.recommendedAction,
        sourceFiles: data.sourceFiles || (Array.isArray(data.docsUsed) ? data.docsUsed.map((d) => d.fileName) : []),
        docsUsed: data.docsUsed || [],
        insufficientData: data.insufficientData,
        grounded: data.grounded,
        fallback: r.fallback,
      }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', content: `Error: ${e.message}` }]);
    } finally { setBusy(false); }
  }

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-4">
      <div className="panel p-0 overflow-hidden flex flex-col h-[70vh] min-h-[500px]">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((m, i) => <ChatBubble key={i} m={m} />)}
          {busy && (
            <div className="flex items-center gap-2 text-white/50 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Researching carriers…
            </div>
          )}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-civix-border p-3 flex gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask an underwriting question…" className="input flex-1" disabled={busy} />
          <button type="submit" disabled={busy || !input.trim()} className="btn-primary flex items-center gap-2">
            <Send className="w-4 h-4" /> Ask
          </button>
        </form>
      </div>

      {/* Quick prompts */}
      <div className="space-y-3">
        <div className="panel p-4">
          <div className="text-xs uppercase tracking-wider text-white/40 mb-3">Quick questions</div>
          <div className="space-y-2">
            {QUICK.map((q, i) => (
              <button key={i} onClick={() => send(q)} disabled={busy}
                className="w-full text-left text-sm p-2 rounded-md border border-white/5 hover:border-civix-purple/40 hover:bg-civix-purple/5 transition text-white/80">
                {q}
              </button>
            ))}
          </div>
        </div>
        <div className="panel p-4 text-xs text-white/40 flex items-start gap-2">
          <Shield className="w-3.5 h-3.5 shrink-0 mt-0.5 text-civix-cyan" />
          Grounded only in your uploaded carrier grids and active roster. No public web data is used. Always verify with the carrier's underwriting desk before issuing.
        </div>
      </div>
    </div>
  );
}

function ChatBubble({ m }) {
  const [showBreakdown, setShowBreakdown] = useState(false);
  if (m.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] bg-civix-purple/20 border border-civix-purple/30 rounded-lg rounded-br-sm px-4 py-2.5 text-sm whitespace-pre-wrap">{m.content}</div>
      </div>
    );
  }
  return (
    <div className="flex gap-3">
      <div className="w-8 h-8 rounded-full bg-linear-to-br from-civix-purple to-civix-cyan flex items-center justify-center shrink-0">
        <Brain className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1 max-w-[90%] space-y-2">
        <div className="bg-white/5 border border-white/10 rounded-lg rounded-tl-sm px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed">{m.content}</div>

        {m.details && (
          <div className="text-xs text-white/70 bg-white/3 border border-white/5 rounded-lg px-3 py-2 whitespace-pre-wrap">{m.details}</div>
        )}

        {m.carrierBreakdown?.length > 0 && (
          <div>
            <button onClick={() => setShowBreakdown((s) => !s)} className="text-xs text-civix-cyan flex items-center gap-1 mb-2 hover:underline">
              <ChevronDown className={`w-3.5 h-3.5 transition ${showBreakdown ? 'rotate-180' : ''}`} />
              Carrier breakdown ({m.carrierBreakdown.length})
            </button>
            {showBreakdown && (
              <div className="rounded-lg border border-white/10 overflow-hidden">
                <table className="w-full text-xs">
                  <thead><tr className="border-b border-white/10 bg-white/5">
                    <th className="text-left px-3 py-1.5 text-white/50">Carrier</th>
                    <th className="text-left px-3 py-1.5 text-white/50">Stance</th>
                    <th className="text-left px-3 py-1.5 text-white/50">Notes</th>
                  </tr></thead>
                  <tbody>
                    {m.carrierBreakdown.map((row, i) => (
                      <tr key={i} className="border-b border-white/5 last:border-0">
                        <td className="px-3 py-1.5 font-medium">{row.carrier}</td>
                        <td className="px-3 py-1.5"><StanceBadge stance={row.stance} /></td>
                        <td className="px-3 py-1.5 text-white/70">{row.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {m.recommendedAction && (
          <div className="rounded-lg bg-civix-cyan/10 border border-civix-cyan/30 px-3 py-2 text-xs text-civix-cyan">
            <span className="font-semibold">Recommended action: </span>{m.recommendedAction}
          </div>
        )}

        {m.insufficientData && (
          <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-xs text-amber-300">
            <span className="font-semibold">Insufficient data: </span>
            Your uploaded grids don't fully cover this question. Upload more carrier docs in the Doc Library tab, or call the carrier UW desk to confirm.
          </div>
        )}

        {m.sourceFiles?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-[10px] text-white/40 uppercase tracking-wider">From your docs:</span>
            {m.sourceFiles.slice(0, 6).map((f, i) => (
              <span key={i} className="text-[10px] inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full border border-civix-purple/30 text-civix-purple bg-civix-purple/5">
                {typeof f === 'string' ? f : f.fileName}
              </span>
            ))}
          </div>
        )}
        {m.fallback && <span className="text-[10px] text-amber-300/60">demo / no API key</span>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab 2 — Carrier Roster Manager
// ─────────────────────────────────────────────────────────────────────────────
function CarrierRosterManager({ onGoToLibrary }) {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [addCarrierId, setAddCarrierId] = useState('');
  const [addStatus, setAddStatus] = useState('active');
  const [addNotes, setAddNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['carrier-roster'],
    queryFn: () => apiGet('/carrier-roster'),
  });
  const roster = data?.data?.roster || [];
  const available = data?.data?.availableCarriers || [];

  const active = roster.filter((r) => r.status === 'active');
  const pending = roster.filter((r) => r.status === 'pending');
  const blacklisted = roster.filter((r) => r.status === 'blacklisted');

  async function changeStatus(carrierId, status) {
    try {
      await apiPut(`/carrier-roster/${carrierId}`, { status });
      qc.invalidateQueries({ queryKey: ['carrier-roster'] });
      toast.success('Carrier updated');
    } catch (e) { toast.error(e.message); }
  }

  async function removeCarrier(carrierId) {
    try {
      await apiDel(`/carrier-roster/${carrierId}`);
      qc.invalidateQueries({ queryKey: ['carrier-roster'] });
      toast.success('Removed');
    } catch (e) { toast.error(e.message); }
  }

  async function addCarrier() {
    if (!addCarrierId) return;
    setSaving(true);
    try {
      await apiPost('/carrier-roster', { carrierId: addCarrierId, status: addStatus, notes: addNotes });
      qc.invalidateQueries({ queryKey: ['carrier-roster'] });
      toast.success('Carrier added');
      setAddOpen(false); setAddCarrierId(''); setAddStatus('active'); setAddNotes('');
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  }

  if (isLoading) return <div className="text-white/40 text-sm p-4">Loading roster…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-white/60">Manage which carriers your bot recommends and avoids</div>
        <button onClick={() => setAddOpen(true)} className="btn-primary flex items-center gap-2 text-sm">
          <Plus className="w-4 h-4" /> Add Carrier
        </button>
      </div>

      <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 px-4 py-2.5 text-xs text-amber-300 flex items-center gap-2">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
        The Carrier Knowledge Bot will <strong>never recommend blacklisted carriers</strong> to your leads.
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {[
          { key: 'active', label: 'Active', items: active, color: 'emerald', nextStatuses: ['pending', 'blacklisted'] },
          { key: 'pending', label: 'Pending', items: pending, color: 'amber', nextStatuses: ['active', 'blacklisted'] },
          { key: 'blacklisted', label: 'Blacklisted', items: blacklisted, color: 'red', nextStatuses: ['active', 'pending'] },
        ].map(({ key, label, items, color, nextStatuses }) => (
          <div key={key} className="panel p-4">
            <div className={`flex items-center gap-2 mb-3 text-${color}-400 font-semibold`}>
              <div className={`w-2 h-2 rounded-full bg-${color}-400`} />
              {label} <span className="ml-auto text-white/50 text-xs font-normal">{items.length}</span>
            </div>
            {items.length === 0 && <div className="text-xs text-white/30">None</div>}
            <div className="space-y-2">
              {items.map((entry) => (
                <div key={entry.carrierId} className="bg-white/5 border border-white/5 rounded-lg p-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-sm font-medium">{entry.carrierName}</div>
                      {entry.notes && <div className="text-xs text-white/50 mt-0.5">{entry.notes}</div>}
                      <div className="flex items-center gap-2 mt-1.5 text-[10px] text-white/40">
                        <FileBadge className="w-3 h-3" />
                        {entry.docCount || 0} doc{entry.docCount !== 1 ? 's' : ''}
                        {(entry.docCount || 0) === 0 && (
                          <button onClick={onGoToLibrary} className="text-civix-cyan hover:underline">Upload</button>
                        )}
                      </div>
                    </div>
                    <button onClick={() => removeCarrier(entry.carrierId)} className="text-white/30 hover:text-red-400 shrink-0">
                      <XCircle className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex gap-1 mt-2">
                    {nextStatuses.map((s) => (
                      <button key={s} onClick={() => changeStatus(entry.carrierId, s)}
                        className="text-[10px] px-2 py-0.5 rounded-full border border-white/10 text-white/50 hover:border-civix-purple/50 hover:text-white capitalize transition">
                        → {s}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Add carrier modal */}
      {addOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="panel p-6 w-full max-w-md space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-semibold">Add Carrier to Roster</h3>
              <button onClick={() => setAddOpen(false)} className="text-white/40 hover:text-white"><XCircle className="w-4 h-4" /></button>
            </div>
            <div>
              <label className="label mb-1">Carrier</label>
              <select value={addCarrierId} onChange={(e) => setAddCarrierId(e.target.value)} className="input w-full">
                <option value="">Select a carrier…</option>
                {available.filter((c) => !roster.find((r) => r.carrierId === c.id)).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label mb-1">Status</label>
              <select value={addStatus} onChange={(e) => setAddStatus(e.target.value)} className="input w-full">
                <option value="active">Active — recommend this carrier</option>
                <option value="pending">Pending — contracting in progress</option>
                <option value="blacklisted">Blacklisted — never recommend</option>
              </select>
            </div>
            <div>
              <label className="label mb-1">Notes (optional)</label>
              <input value={addNotes} onChange={(e) => setAddNotes(e.target.value)} placeholder="e.g. Great for FEX, fast issue" className="input w-full" />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setAddOpen(false)} className="btn-ghost">Cancel</button>
              <button onClick={addCarrier} disabled={!addCarrierId || saving} className="btn-primary">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab 3 — Underwriting Doc Library
// ─────────────────────────────────────────────────────────────────────────────
function UnderwritingDocLibrary() {
  const qc = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [filterCarrier, setFilterCarrier] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedDoc, setSelectedDoc] = useState(null);

  const { data: rosterData } = useQuery({ queryKey: ['carrier-roster'], queryFn: () => apiGet('/carrier-roster') });
  const availableCarriers = rosterData?.data?.availableCarriers || [];

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['underwriting-docs', filterCarrier, filterStatus],
    queryFn: () => apiGet('/underwriting-docs', { carrierId: filterCarrier || undefined, status: filterStatus || undefined }),
    refetchInterval: 5000, // poll for extraction status
  });
  const docs = data?.data?.docs || [];

  async function deleteDoc(id) {
    if (!window.confirm('Delete this document?')) return;
    try {
      await apiDel(`/underwriting-docs/${id}`);
      qc.invalidateQueries({ queryKey: ['underwriting-docs'] });
      toast.success('Deleted');
    } catch (e) { toast.error(e.message); }
  }

  async function viewDoc(id) {
    try {
      const r = await apiGet(`/underwriting-docs/${id}`);
      setSelectedDoc(r.data?.doc || null);
    } catch (e) { toast.error(e.message); }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex gap-2">
          <select value={filterCarrier} onChange={(e) => setFilterCarrier(e.target.value)} className="input text-sm">
            <option value="">All carriers</option>
            {availableCarriers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="input text-sm">
            <option value="">All statuses</option>
            <option value="ready">Ready</option>
            <option value="extracting">Extracting</option>
            <option value="uploaded">Uploaded</option>
            <option value="error">Error</option>
          </select>
          <button onClick={() => refetch()} className="btn-ghost text-sm"><RefreshCw className="w-4 h-4" /></button>
        </div>
        <button onClick={() => setUploadOpen(true)} className="btn-primary flex items-center gap-2 text-sm">
          <Upload className="w-4 h-4" /> Upload Document
        </button>
      </div>

      {isLoading && <div className="text-white/40 text-sm">Loading documents…</div>}
      {!isLoading && docs.length === 0 && (
        <div className="panel p-8 text-center text-white/40">
          <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <div>No documents yet. Upload carrier underwriting grids and spec sheets.</div>
        </div>
      )}

      <div className="grid gap-3">
        {docs.map((doc) => (
          <div key={doc._id} className="panel p-4 flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-civix-purple/20 flex items-center justify-center shrink-0">
                <FileBadge className="w-5 h-5 text-civix-purple" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm truncate">{doc.fileName}</div>
                <div className="text-xs text-white/50 mt-0.5">{doc.carrierName} · {doc.product || 'All products'} · {doc.docType}</div>
                <div className="flex items-center gap-2 mt-1.5">
                  <StatusBadge status={doc.status} />
                  {doc.status === 'ready' && <span className="text-[10px] text-white/40">{doc.extractedRules?.length || 0} rules extracted</span>}
                  <span className="text-[10px] text-white/30">{(doc.fileSize / 1024).toFixed(1)} KB</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {doc.status === 'ready' && (
                <button onClick={() => viewDoc(doc._id)} className="btn-ghost text-xs flex items-center gap-1">
                  <Info className="w-3.5 h-3.5" /> Rules
                </button>
              )}
              <button onClick={() => deleteDoc(doc._id)} className="text-white/30 hover:text-red-400">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Upload modal */}
      {uploadOpen && <UploadModal onClose={() => setUploadOpen(false)} onSuccess={() => { qc.invalidateQueries({ queryKey: ['underwriting-docs'] }); setUploadOpen(false); }} carriers={availableCarriers} />}

      {/* Rules drawer */}
      {selectedDoc && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-end p-4">
          <div className="panel p-5 w-full max-w-lg h-full max-h-[90vh] overflow-y-auto space-y-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-display font-semibold">{selectedDoc.carrierName} — Rules</h3>
                <div className="text-xs text-white/50">{selectedDoc.fileName}</div>
              </div>
              <button onClick={() => setSelectedDoc(null)} className="text-white/40 hover:text-white shrink-0">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            {selectedDoc.extractedRules?.length > 0 ? (
              <div className="space-y-2">
                {selectedDoc.extractedRules.map((rule, i) => (
                  <div key={i} className="bg-white/5 rounded-lg p-3 text-sm">
                    <div className="font-medium">{rule.condition}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <StanceBadge stance={rule.ruling} />
                      {rule.rateClass && <span className="text-[10px] text-white/50">{rule.rateClass}</span>}
                    </div>
                    {rule.notes && <div className="text-xs text-white/60 mt-1">{rule.notes}</div>}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-white/40 text-sm">No structured rules extracted yet.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function UploadModal({ onClose, onSuccess, carriers }) {
  const [file, setFile] = useState(null);
  const [carrierId, setCarrierId] = useState('');
  const [carrierName, setCarrierName] = useState('');
  const [product, setProduct] = useState('');
  const [docType, setDocType] = useState('grid');
  const [uploading, setUploading] = useState(false);

  function pickCarrier(id) {
    setCarrierId(id);
    const c = carriers.find((x) => x.id === id);
    setCarrierName(c?.name || '');
  }

  async function doUpload() {
    if (!file || !carrierId) { toast.error('File and carrier required'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('carrierId', carrierId);
      fd.append('carrierName', carrierName);
      fd.append('product', product);
      fd.append('docType', docType);
      const token = localStorage.getItem('civix_token');
      const base = import.meta.env.VITE_API_URL || '/api';
      const resp = await fetch(`${base}/underwriting-docs`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      const json = await resp.json();
      if (!json.ok) throw new Error(json.error || 'Upload failed');
      toast.success('Uploaded! Rules extracting in background…');
      onSuccess();
    } catch (e) { toast.error(e.message); }
    setUploading(false);
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="panel p-6 w-full max-w-md space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-semibold">Upload Underwriting Document</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white"><XCircle className="w-4 h-4" /></button>
        </div>
        <div>
          <label className="label mb-1">PDF File</label>
          <input type="file" accept=".pdf" onChange={(e) => setFile(e.target.files[0])} className="input w-full text-sm" />
          {file && <div className="text-xs text-white/40 mt-1">{file.name} ({(file.size / 1024).toFixed(1)} KB)</div>}
        </div>
        <div>
          <label className="label mb-1">Carrier</label>
          <select value={carrierId} onChange={(e) => pickCarrier(e.target.value)} className="input w-full">
            <option value="">Select carrier…</option>
            {carriers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label mb-1">Product (optional)</label>
            <input value={product} onChange={(e) => setProduct(e.target.value)} placeholder="e.g. Final Expense" className="input w-full" />
          </div>
          <div>
            <label className="label mb-1">Doc Type</label>
            <select value={docType} onChange={(e) => setDocType(e.target.value)} className="input w-full">
              <option value="grid">Underwriting Grid</option>
              <option value="spec">Product Spec Sheet</option>
              <option value="brochure">Brochure</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={doUpload} disabled={!file || !carrierId || uploading} className="btn-primary flex items-center gap-2">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? 'Uploading…' : 'Upload & Extract'}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    ready: { cls: 'text-emerald-400 bg-emerald-500/15', icon: CheckCircle2, label: 'Ready' },
    extracting: { cls: 'text-blue-400 bg-blue-500/15', icon: Loader2, label: 'Extracting…', spin: true },
    uploaded: { cls: 'text-amber-400 bg-amber-500/15', icon: Clock, label: 'Uploaded' },
    error: { cls: 'text-red-400 bg-red-500/15', icon: AlertTriangle, label: 'Error' },
  };
  const s = map[status] || map.uploaded;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${s.cls}`}>
      <Icon className={`w-3 h-3 ${s.spin ? 'animate-spin' : ''}`} />{s.label}
    </span>
  );
}

function StanceBadge({ stance }) {
  const map = {
    approves: 'text-emerald-400 bg-emerald-500/15',
    approve: 'text-emerald-400 bg-emerald-500/15',
    declines: 'text-red-400 bg-red-500/15',
    decline: 'text-red-400 bg-red-500/15',
    rated: 'text-amber-400 bg-amber-500/15',
    refer: 'text-blue-400 bg-blue-500/15',
    varies: 'text-blue-400 bg-blue-500/15',
  };
  const cls = map[stance?.toLowerCase()] || 'text-white/50 bg-white/5';
  return <span className={`px-1.5 py-0.5 rounded-full text-[10px] capitalize ${cls}`}>{stance || 'varies'}</span>;
}

function hostnameOf(url) {
  try { return new URL(url).hostname.replace(/^www\./, ''); }
  catch { return 'source'; }
}
